import {
  cloneConfiguration, controlSelector, describeMultiPositionSteps,
  dimensionNumber, samePositionPayload, updateMultiPositionField,
} from './multiPositionFields';
import { requiresUnsupportedPowderFee } from './ConfiguratorContext';
import { resolveMultiPositionProduct } from './multiPositionProducts';
import { priceIncludingTeuerungszuschlag } from './teuerungszuschlag';

const engineProperties = [
  'positionBezeichnung', 'anzahl', 'notizen', 'price', 'addedItems', 'currentRalPricing', 'selectedRalHex',
  'isAlternativePosition', 'fittingOversizeWarning', 'fittingDimensionNoticeStepId', 'priceOnRequest',
];
const editableRow = row => !row.savedId && !['saving', 'uncertain'].includes(row.status);

export default {
  data() {
    return {
      positionEntryMode: 'single', multiDrafts: {}, multiDraft: null, multiSingleSnapshot: null,
      multiSaving: false, multiEvaluating: false, multiApplying: false, positionModeSwitching: false,
      multiEvaluationError: '', multiMessage: '', multiTimer: null, multiEvaluationTask: null,
      multiNextRowId: 1,
      multiCommonErrors: {}, multiProjectId: null,
    };
  },
  computed: {
    multiPositionProduct() { return resolveMultiPositionProduct(this.selectedArticle, this.searchTextArticle); },
    isExistingPosition() {
      return this.positionIsEdited || (this.selectedArticleId != null && this.selectedArticleId !== '' && Number(this.selectedArticleId) >= 0);
    },
    multiPositionAvailable() { return !!this.multiPositionProduct && !this.isExistingPosition; },
    isMultiPositionActive() { return this.positionEntryMode === 'multi' && this.multiPositionAvailable; },
    multiSchema() {
      return this.multiPositionProduct ? describeMultiPositionSteps(this.filteredStepItems, this.multiPositionProduct.extension)
        : { fields: [], positionStepIds: [], calculations: [], errors: [] };
    },
    multiColumns() {
      return this.multiSchema.fields.filter(field => field.alwaysVisible || (this.multiDraft?.rows || [])
        .some(row => row.availability[field.key]?.visible));
    },
    multiHasUncertainRows() { return this.multiDraft?.rows.some(row => row.status === 'uncertain') || false; },
    multiCanSave() {
      return this.isMultiPositionActive && !!this.selectedProject?.id && !this.positionModeSwitching && !this.multiSaving && !this.multiHasUncertainRows && !this.isSelectedProjectReadOnly
        && this.isCurrentMultiDraft() && !this.multiSchema.errors.length && this.multiDraft?.rows.some(editableRow);
    },
    multiCanFinalize() {
      return this.isMultiPositionActive && !!this.selectedProject?.id && !!this.multiDraft?.finalizationPending && !this.multiSaving
        && this.isCurrentMultiDraft() && !this.multiEvaluating && !this.isSelectedProjectReadOnly;
    },
  },
  watch: {
    'selectedProject.id'(id) { this.synchronizeMultiProject(id); },
  },
  beforeUnmount() { clearTimeout(this.multiTimer); },
  methods: {
    synchronizeMultiProject(id) {
      if (String(id) === String(this.multiProjectId)) return;
      const previous = this.multiProjectId;
      this.multiProjectId = id ?? null;
      if (previous == null) return;
      clearTimeout(this.multiTimer);
      this.positionEntryMode = 'single';
      this.multiDraft = null;
      this.multiSingleSnapshot = null;
      this.multiCommonErrors = {};
      this.selectedArticleId = -1;
      this.positionIsEdited = false;
    },
    isCurrentMultiDraft(draft = this.multiDraft) {
      return this.multiPositionAvailable && draft?.key === `${this.selectedProject?.id}:${this.searchTextArticle}`;
    },
    multiDisplayPrice(row) {
      return this.isExternal ? row.price : priceIncludingTeuerungszuschlag(row.price, this.teuerungszuschlagRate);
    },
    async beginNewPosition() {
      if (this.multiSaving || this.multiEvaluating || this.positionModeSwitching) return false;
      if (this.positionEntryMode === 'multi') await this.setPositionEntryMode('single');
      this.clearFields();
      return true;
    },
    isMultiRowStep(step) { return this.multiSchema.positionStepIds.includes(String(step.id)); },
    isMultiRowEditable(row) {
      return this.isCurrentMultiDraft() && this.multiDraft.rows.includes(row) && editableRow(row) && !this.multiSaving && !this.isSelectedProjectReadOnly;
    },
    captureMultiFields() {
      const fields = {};
      for (const step of this.filteredStepItems) {
        const element = this.$refs.dynamicContent?.querySelector(`#s_${step.id}`);
        element?.querySelectorAll(controlSelector).forEach((control, index) => {
          fields[`${step.id}:${index}`] = {
            value: control.value, checked: control.checked || false, disabled: control.disabled,
            selectedIndex: control.selectedIndex, dataset: { ...control.dataset }, style: control.style.cssText,
          };
        });
      }
      return fields;
    },
    captureMultiEngine() {
      const properties = [...new Set([...engineProperties, ...(this.multiPositionProduct?.extension.engineProperties || [])])];
      return {
        fields: this.captureMultiFields(),
        properties: cloneConfiguration(Object.fromEntries(properties.map(key => [key, this[key]]))),
        hidden: Object.fromEntries(this.filteredStepItems.map(step => [step.id, !!step.isHiddenByRule])),
      };
    },
    restoreMultiFields(fields) {
      for (const step of this.filteredStepItems) {
        this.$refs.dynamicContent?.querySelector(`#s_${step.id}`)?.querySelectorAll(controlSelector).forEach((control, index) => {
          const state = fields[`${step.id}:${index}`];
          if (!state) return;
          control.value = state.value ?? '';
          if (control.tagName === 'SELECT' && state.selectedIndex !== undefined) control.selectedIndex = state.selectedIndex;
          if ('checked' in control) control.checked = !!state.checked;
          control.disabled = !!state.disabled;
          if (state.dataset) {
            Object.keys(control.dataset).forEach(key => { delete control.dataset[key]; });
            Object.assign(control.dataset, state.dataset);
          }
          control.style.cssText = state.style || '';
          control.classList.remove('input-error', 'out-of-bounds-error');
        });
      }
    },
    async restoreMultiEngine(snapshot) {
      if (!snapshot) return;
      Object.assign(this, cloneConfiguration(snapshot.properties));
      this.filteredStepItems.forEach(step => { step.isHiddenByRule = snapshot.hidden[step.id]; });
      await this.$nextTick();
      this.restoreMultiFields(snapshot.fields);
      this.clearRasterDimensionErrors();
    },
    splitMultiFields(fields, positionSpecific) {
      const ids = new Set(this.multiSchema.positionStepIds);
      return Object.fromEntries(Object.entries(fields).filter(([key]) => ids.has(key.split(':')[0]) === positionSpecific));
    },
    async settleSinglePositionPrice() {
      clearTimeout(this.calcTimerId);
      this.calcTimerId = null;
      // An older request may still finish with dimensions captured before the last edit.
      while (this.isCalculatingPrice) await new Promise(resolve => setTimeout(resolve, 25));
      clearTimeout(this.calcTimerId);
      this.calcTimerId = null;
      await this.calculatePriceFromScratchSafe();
      this.refreshConfiguredArticle();
    },
    async setPositionEntryMode(mode) {
      if (mode === this.positionEntryMode || this.multiSaving || this.multiEvaluating || this.positionModeSwitching) return;
      if (mode === 'multi' && (!this.multiPositionAvailable || !this.selectedProject?.id || !this.ensureProjectEditable())) return;
      this.positionModeSwitching = true;
      try {
        if (mode === 'multi') {
          const projectId = this.selectedProject.id;
          const product = this.searchTextArticle;
          await this.settleSinglePositionPrice();
          if (this.selectedProject?.id !== projectId || this.searchTextArticle !== product || !this.multiPositionAvailable) return;
          this.multiSingleSnapshot = this.captureMultiEngine();
          const key = `${this.selectedProject.id}:${this.searchTextArticle}`;
          if (!this.multiDrafts[key] || this.multiDrafts[key].completed) {
            this.multiDrafts[key] = {
              key, commonFields: this.splitMultiFields(this.multiSingleSnapshot.fields, false), rows: [], finalizationPending: false,
            };
            this.multiDraft = this.multiDrafts[key];
            this.addMultiPosition(this.multiSingleSnapshot);
          } else this.multiDraft = this.multiDrafts[key];
          this.positionEntryMode = 'multi';
          this.multiMessage = this.multiDraft.finalizationPending ? 'Der Abschluss ist noch nicht bestätigt. Bitte den Abschluss wiederholen.' : '';
          this.multiApplying = true;
          this.restoreMultiFields(this.multiDraft.commonFields);
          await this.$nextTick();
          this.multiApplying = false;
          this.queueMultiEvaluation();
        } else {
          clearTimeout(this.multiTimer);
          this.multiCommonErrors = {};
          this.positionEntryMode = 'single';
          this.multiApplying = true;
          await this.restoreMultiEngine(this.multiSingleSnapshot);
          this.multiApplying = false;
          this.refreshEventListener();
          await this.settleSinglePositionPrice();
        }
      } finally { this.positionModeSwitching = false; }
    },
    addMultiPosition(snapshot = null) {
      if (!this.isCurrentMultiDraft() || this.multiSaving || this.isSelectedProjectReadOnly) return;
      if (this.multiDraft.completed) {
        this.multiDraft = { key: this.multiDraft.key, commonFields: this.multiDraft.commonFields, rows: [], finalizationPending: false };
        this.multiDrafts[this.multiDraft.key] = this.multiDraft;
      }
      const initialFields = Object.fromEntries(this.multiSchema.fields.map(field => [field.key, cloneConfiguration(field.initial)]));
      this.multiDraft.rows.push({
        key: this.multiNextRowId++, fields: snapshot ? this.splitMultiFields(snapshot.fields, true) : initialFields,
        room: snapshot?.properties.positionBezeichnung || '', amount: snapshot?.properties.anzahl ?? 1,
        notes: snapshot?.properties.notizen || '',
        isAlternativePosition: snapshot?.properties.isAlternativePosition || false,
        availability: {}, errors: [], fieldErrors: {}, commonErrors: {}, generalErrors: [],
        status: 'draft', price: null, priceOnRequest: false, oversizeMessage: '', configuration: null,
        revision: 0, evaluatedRevision: -1, savedId: null, pendingControls: [],
      });
      if (this.isMultiPositionActive) {
        this.queueMultiEvaluation();
        const key = this.multiDraft.rows.at(-1).key;
        this.$nextTick(() => this.$el.querySelector(`[data-multi-row="${key}"] input:not(:disabled)`)?.focus());
      }
    },
    removeMultiPosition(row) {
      if (!this.isMultiRowEditable(row)) return;
      this.multiDraft.rows.splice(this.multiDraft.rows.indexOf(row), 1);
    },
    changeMultiRow(row, field, value) {
      if (!this.isMultiRowEditable(row)) return;
      if (typeof field === 'string') row[field] = value;
      else {
        updateMultiPositionField(row, field, value, this.multiSchema);
        row.pendingControls = [...row.pendingControls.filter(key => key !== field.key), field.key];
      }
      row.revision += 1;
      row.status = 'draft';
      const changedKeys = typeof field === 'string' ? [field] : [field.key,
        ...this.multiSchema.calculations.filter(calculation => calculation.sourceKeys.includes(field.key)).map(calculation => calculation.targetKey)];
      row.fieldErrors = { ...row.fieldErrors };
      changedKeys.forEach(key => { delete row.fieldErrors[key]; });
      row.generalErrors = [];
      row.errors = [...Object.values(row.fieldErrors), ...Object.values(row.commonErrors)];
      row.oversizeMessage = '';
      row.configuration = null;
      row.price = null;
    },
    onMultiCommonInput(event) {
      if (!this.isMultiPositionActive || this.multiApplying || this.multiSaving) return;
      const step = event.target.closest('[id^="s_"]');
      if (!step || this.multiSchema.positionStepIds.includes(step.id.slice(2))) return;
      this.multiDraft.commonFields = this.splitMultiFields(this.captureMultiFields(), false);
      this.multiDraft.rows.filter(editableRow).forEach(row => {
        row.revision += 1;
        row.status = 'draft';
        row.configuration = null;
        row.price = null;
        row.errors = [];
        row.fieldErrors = {};
        row.commonErrors = {};
        row.generalErrors = [];
      });
      this.multiCommonErrors = {};
      this.queueMultiEvaluation();
    },
    queueMultiEvaluation() {
      if (!this.isMultiPositionActive || this.multiSaving) return;
      clearTimeout(this.multiTimer);
      this.multiTimer = setTimeout(() => { this.multiTimer = null; this.evaluateMultiPositions(); }, 300);
    },
    async evaluateMultiPositions(force = false) {
      if (this.multiEvaluationTask) {
        await this.multiEvaluationTask;
        if (force) return this.evaluateMultiPositions(true);
        this.queueMultiEvaluation();
        return;
      }
      if (!this.isMultiPositionActive || !this.isCurrentMultiDraft() || this.multiSchema.errors.length) return;
      const draft = this.multiDraft;
      const rows = this.multiDraft.rows.filter(row => editableRow(row) && (force || row.evaluatedRevision !== row.revision));
      if (!rows.length) return;
      const snapshot = this.captureMultiEngine();
      this.multiEvaluating = true;
      this.multiApplying = true;
      this.multiEvaluationTask = (async () => {
        try {
          for (const row of rows) {
            if (this.multiDraft !== draft || !this.isCurrentMultiDraft(draft)) break;
            try { await this.evaluateMultiPosition(row); }
            catch {
              row.status = 'invalid';
              row.configuration = null;
              row.price = null;
              row.errors = ['Diese Position konnte nicht vollständig ausgewertet werden. Bitte die Einzelposition verwenden.'];
              row.generalErrors = [...row.errors];
              row.fieldErrors = {};
            }
          }
        } finally {
          if (this.multiDraft === draft && this.isCurrentMultiDraft(draft)) {
            await this.restoreMultiEngine(snapshot);
            this.filterStepsByStepRules();
            this.multiCommonErrors = Object.assign({}, ...draft.rows.filter(editableRow).map(row => row.commonErrors));
          }
          await this.$nextTick();
          this.multiApplying = false;
          this.multiEvaluating = false;
          this.multiEvaluationTask = null;
        }
      })();
      await this.multiEvaluationTask;
    },
    async evaluateMultiPosition(row) {
      const revision = row.revision;
      const rowFields = cloneConfiguration(row.fields);
      const commonFields = cloneConfiguration(this.multiDraft.commonFields);
      const pendingControls = [...row.pendingControls];
      row.status = 'checking';
      this.multiEvaluationError = '';
      const extensionContext = { engine: this, row, schema: this.multiSchema };
      await this.multiPositionProduct.extension.resetEngine?.(extensionContext);
      this.isAlternativePosition = row.isAlternativePosition;
      this.filteredStepItems.forEach(step => { step.isHiddenByRule = false; });
      await this.$nextTick();
      this.restoreMultiFields({ ...commonFields, ...rowFields });
      this.positionBezeichnung = row.room;
      this.anzahl = row.amount;
      this.notizen = row.notes;
      this.clearRasterDimensionErrors();
      for (const key of pendingControls) {
        const [stepId, index] = key.split(':');
        const control = this.$refs.dynamicContent.querySelector(`#s_${stepId}`)?.querySelectorAll(controlSelector)[Number(index)];
        if (!control) continue;
        // Replay edits in order so later manual corrections take precedence.
        control.value = rowFields[key].value;
        if (control.tagName === 'SELECT') control.selectedIndex = rowFields[key].selectedIndex;
        if ('checked' in control) control.checked = !!rowFields[key].checked;
        if (!['checkbox', 'select-one'].includes(control.type)) control.dispatchEvent(new Event('input', { bubbles: true }));
        control.dispatchEvent(new Event('change', { bubbles: true }));
      }
      this.applyInputOverrideCheckboxes();
      await this.multiPositionProduct.extension.prepareEvaluation?.(extensionContext);
      this.filterStepsByStepRules();
      await this.$nextTick();
      await this.multiPositionProduct.extension.beforePrice?.(extensionContext);
      await this.calculatePriceFromScratchSafe();
      await this.$nextTick();
      this.refreshConfiguredArticle();
      const validation = this.validateMultiPosition(row);
      const configuration = this.getConfigurationAsJSON();
      if (this.isExternal && requiresUnsupportedPowderFee(JSON.parse(configuration))) {
        validation.generalErrors.push('Diese Oberfläche benötigt eine Pulverpauschale und wird in der externen Integration nicht unterstützt.');
      }
      // A user may already be typing into the next cell while this price request finishes.
      if (revision !== row.revision || !this.isCurrentMultiDraft() || !this.multiDraft.rows.includes(row)) return;
      const errors = [...new Set([...Object.values(validation.fieldErrors), ...Object.values(validation.commonErrors), ...validation.generalErrors])];
      row.fields = this.splitMultiFields(this.captureMultiFields(), true);
      row.availability = this.captureMultiAvailability();
      row.errors = [...new Set(errors)];
      row.fieldErrors = validation.fieldErrors;
      row.commonErrors = validation.commonErrors;
      row.generalErrors = validation.generalErrors;
      row.price = errors.length ? null : this.price;
      row.priceOnRequest = this.multiPositionProduct.extension.isPriceOnRequest?.(this.price) ?? this.price <= 0;
      row.oversizeMessage = this.fittingOversizeWarning ? this.selectedFittingDimensionRule?.oversize_message || '' : '';
      row.configuration = errors.length ? null : configuration;
      row.status = errors.length ? 'invalid' : 'ready';
      row.evaluatedRevision = revision;
      row.pendingControls = [];
    },
    captureMultiAvailability() {
      return Object.fromEntries(this.multiSchema.fields.map(field => {
        const step = this.$refs.dynamicContent.querySelector(`#s_${field.stepId}`);
        const control = step?.querySelectorAll(controlSelector)[field.index];
        let visible = !!control && !this.isStepHidden(step);
        for (let parent = control; visible && parent && parent !== step; parent = parent.parentElement) {
          const style = getComputedStyle(parent);
          if (parent.hidden || style.display === 'none' || style.visibility === 'hidden') visible = false;
        }
        return [field.key, { visible, disabled: !!control?.disabled || !!control?.readOnly }];
      }));
    },
    validateMultiPosition(row) {
      const fieldErrors = {};
      const commonErrors = {};
      const generalErrors = [];
      const append = (errors, key, message) => {
        errors[key] = [...new Set([errors[key], message].filter(Boolean))].join(' ');
      };
      if (!Number.isSafeInteger(Number(row.amount)) || Number(row.amount) < 1 || !/^\d+$/.test(String(row.amount))) {
        fieldErrors.amount = 'Die Anzahl muss eine positive ganze Zahl sein.';
      }
      if (!/^[A-Za-z0-9]{0,7}$/.test(row.room)) fieldErrors.room = 'Die Raumbezeichnung darf maximal 7 Buchstaben oder Ziffern enthalten.';
      if (row.notes.length > 200) fieldErrors.notes = 'Die Notiz darf maximal 200 Zeichen enthalten.';
      const availability = this.captureMultiAvailability();
      generalErrors.push(...(this.multiPositionProduct.extension.validate?.({ engine: this, row, schema: this.multiSchema, availability }) || []));
      for (const step of this.filteredStepItems) {
        const element = this.$refs.dynamicContent.querySelector(`#s_${step.id}`);
        if (!element || this.isStepHidden(element)) continue;
        if (Number(step.is_mandatory) && !element.querySelector(controlSelector)) {
          generalErrors.push(`${step.name}: Der Pflichtschritt konnte nicht ausgewertet werden.`);
        }
        element.querySelectorAll(controlSelector).forEach((control, index) => {
          const key = `${step.id}:${index}`;
          const field = this.multiSchema.fields.find(candidate => candidate.key === key);
          if (control.type === 'hidden' || (field && !availability[key]?.visible)) return;
          for (let parent = control; parent && parent !== element; parent = parent.parentElement) {
            const style = getComputedStyle(parent);
            if (parent.hidden || style.display === 'none' || style.visibility === 'hidden') return;
          }
          const errors = field ? fieldErrors : commonErrors;
          const label = field?.label || control.closest('.input-container')?.querySelector('.container-input-label')?.textContent.replace(/:\s*$/, '').trim() || step.name;
          const fail = message => append(errors, key, `${label}: ${message}`);
          const text = String(control.value ?? '').trim();
          if (!control.disabled && (control.required || control.classList.contains('mandatory-field') || Number(step.is_mandatory))
              && (!text || control.type === 'checkbox' && !control.checked)) fail('Pflichtfeld ausfüllen.');
          const numeric = field?.numeric || control.type === 'number';
          const value = dimensionNumber(text);
          if (numeric && text && !control.disabled && (value === null || field?.positive && value <= 0)) {
            fail('Bitte eine gültige positive Zahl eingeben.');
          }
          if (numeric && value !== null) {
            // Reuse the engine's effective bounds, including final RAM/ZAM rules.
            const bounds = this.getFittingDimensionBounds(control);
            const minimums = [dimensionNumber(control.getAttribute('min'))];
            const maximums = [dimensionNumber(control.getAttribute('max'))];
            if (bounds) {
              minimums.push(bounds.min);
              maximums.push(bounds.max);
            } else {
              if (control.classList.contains('min_value')) minimums.push(this.getMinDimensionValue(control));
              if (field?.axis || control.hasAttribute('data-max-value')) maximums.push(this.getMaxDimensionValue(control, field?.axis));
            }
            const min = Math.max(...minimums.filter(value => value != null));
            const max = Math.min(...maximums.filter(value => value != null));
            const unit = field?.unit ? ` ${field.unit}` : '';
            if (value < min) fail(`Mindestens ${min}${unit}.`);
            if (value > max) fail(`Maximal ${max}${unit}.`);
          }
          if (control.classList.contains('raster-dimension-error')) fail('Diese Kombination aus Breite und Höhe ist nicht möglich.');
          else if (control.matches('.input-error, .out-of-bounds-error') && !errors[key]) {
            fail('Bitte Maßgrenzen und abhängige Eingaben prüfen.');
          }
          if (!control.disabled && control.validity && !control.validity.valid && !errors[key]) fail('Bitte eine gültige Eingabe wählen.');
        });
      }
      if (this.rasterDimensionError && !Object.values(fieldErrors).some(message => message.includes('Kombination'))) generalErrors.push(this.rasterDimensionError);
      if (this.multiEvaluationError) generalErrors.push(this.multiEvaluationError);
      if (!Number.isFinite(this.price)) generalErrors.push('Der Preis konnte nicht zuverlässig berechnet werden.');
      return { fieldErrors, commonErrors, generalErrors: [...new Set(generalErrors)] };
    },
    async saveMultiPositions() {
      if (!this.multiCanSave || !this.ensureProjectEditable()) return;
      const projectId = this.selectedProject.id;
      const draft = this.multiDraft;
      this.multiSaving = true;
      this.multiMessage = '';
      clearTimeout(this.multiTimer);
      let targetId = null;
      let attemptedWrite = false;
      try {
        if (draft.finalizationPending && !await this.finalizeMultiPositions(projectId, draft, false)) return;
        await this.evaluateMultiPositions(true);
        const rows = this.multiDraft.rows.filter(editableRow);
        if (rows.some(row => !row.configuration || row.status !== 'ready')) {
          return;
        }
        const project = await this.configuratorApi.loadProject();
        if (project.id !== projectId || this.selectedProject?.id !== projectId || this.multiDraft !== draft || !this.isCurrentMultiDraft(draft)) {
          throw new Error('The project changed during batch validation');
        }
        const knownIds = new Set(project.positions.map(position => String(position.id)));
        for (const row of rows) {
          if (!this.ensureProjectEditable() || this.selectedProject?.id !== projectId || this.multiDraft !== draft || !this.isCurrentMultiDraft(draft)) break;
          row.payload = {
            position_bezeichnung: row.room, artikel_json: row.configuration,
            anzahl: Number(row.amount), notizen: row.notes, projekt_id: projectId,
          };
          row.beforeIds = [...knownIds];
          row.status = 'saving';
          attemptedWrite = true;
          try {
            // An uncertain POST must never be retried by the shared HTTP interceptor.
            const response = await this.configuratorApi.createPosition(row.payload, { skipAutomaticRetry: true });
            if (!response?.data?.id) throw new Error('Missing saved position ID');
            row.savedId = response.data.id;
            targetId = row.savedId;
            knownIds.add(String(row.savedId));
            row.status = 'saved';
            row.errors = [];
            row.generalErrors = [];
            row.fieldErrors = {};
          } catch (error) {
            const status = error.response?.status;
            const rejected = [400, 401, 403, 404, 409, 419, 422, 426, 429].includes(status);
            row.status = rejected ? 'invalid' : 'uncertain';
            const serverErrors = error.response?.data?.errors || {};
            const positionFields = { position_bezeichnung: 'room', anzahl: 'amount', notizen: 'notes' };
            row.fieldErrors = {};
            row.generalErrors = [];
            for (const [key, messages] of Object.entries(serverErrors)) {
              const message = [messages].flat().join(' ');
              if (rejected && positionFields[key]) row.fieldErrors[positionFields[key]] = message;
              else row.generalErrors.push(message);
            }
            if (!Object.keys(serverErrors).length) row.generalErrors.push(rejected
              ? 'Speichern fehlgeschlagen. Bitte Eingaben und Berechtigung prüfen.'
              : 'Keine eindeutige Speicherbestätigung. Vor einer weiteren Speicherung den Status prüfen.');
            row.errors = [...Object.values(row.fieldErrors), ...row.generalErrors];
            if (!rejected || [401, 403, 409, 419, 426].includes(status)) break;
          }
        }
        const complete = this.multiDraft.rows.every(row => !!row.savedId);
        this.multiMessage = complete ? 'Alle Positionen wurden gespeichert.'
          : 'Einige Positionen wurden nicht bestätigt. Bereits gespeicherte Zeilen werden nicht erneut angelegt.';
      } catch {
        this.multiMessage = 'Die Positionen konnten nicht vollständig geprüft oder geladen werden. Bitte erneut versuchen.';
      } finally {
        if (attemptedWrite && this.selectedProject?.id === projectId && this.multiDraft === draft) {
          draft.finalizationPending = true;
          await this.finalizeMultiPositions(projectId, draft, true, targetId);
        }
        this.multiSaving = false;
      }
    },
    async finalizeMultiPositions(projectId, draft, openCart = true, targetId = null) {
      const assertContext = () => {
        if (this.selectedProject?.id !== projectId || this.multiDraft !== draft || !this.isCurrentMultiDraft(draft)) throw new Error('Project changed during finalization');
      };
      try {
        assertContext();
        if (await this.refreshSavedArticles({ syncCart: false }) !== true) throw new Error('Initial project reload failed');
        assertContext();
        await this.synchronizePowderFees({ projectConfirmed: true });
        assertContext();
        if (await this.refreshSavedArticles() !== true) throw new Error('Final project reload failed');
        assertContext();
        const complete = draft.rows.length > 0 && draft.rows.every(row => !!row.savedId);
        if (complete && openCart) await this.openCartAndFocus(targetId || draft.rows.at(-1)?.savedId);
        assertContext();
        draft.finalizationPending = false;
        if (complete) {
          this.multiMessage = 'Alle Positionen wurden gespeichert und der Abschluss wurde bestätigt.';
          // Retire rows only after both reloads, powder fees and cart opening succeeded.
          draft.rows = [];
          draft.completed = true;
          this.multiCommonErrors = {};
          if (this.multiDrafts[draft.key] === draft) delete this.multiDrafts[draft.key];
        }
        return true;
      } catch (error) {
        draft.finalizationPending = true;
        this.multiMessage = 'Abschluss fehlgeschlagen. Gespeicherte Zeilen bleiben geschützt. Bitte den Abschluss wiederholen.'
          + (error.userMessage ? ` ${error.userMessage}` : '');
        return false;
      }
    },
    async retryMultiFinalization() {
      if (!this.multiCanFinalize || !this.ensureProjectEditable()) return;
      this.multiSaving = true;
      try { await this.finalizeMultiPositions(this.selectedProject.id, this.multiDraft); }
      finally { this.multiSaving = false; }
    },
    async resolveMultiSaveStatus() {
      if (!this.isMultiPositionActive || !this.isCurrentMultiDraft() || this.multiSaving || this.multiEvaluating) return;
      this.multiSaving = true;
      const draft = this.multiDraft;
      const projectId = this.selectedProject.id;
      try {
        const project = await this.configuratorApi.loadProject();
        if (project.id !== projectId || this.selectedProject?.id !== projectId || this.multiDraft !== draft) {
          throw new Error('Project changed while resolving save status');
        }
        const claimed = new Set(this.multiDraft.rows.filter(row => row.savedId).map(row => String(row.savedId)));
        for (const row of this.multiDraft.rows.filter(row => row.status === 'uncertain')) {
          const matches = project.positions.filter(position => !claimed.has(String(position.id))
            && !row.beforeIds.includes(String(position.id)) && samePositionPayload(position, row.payload));
          if (matches.length === 1) {
            row.savedId = matches[0].id;
            row.status = 'saved';
            row.errors = [];
            row.generalErrors = [];
            claimed.add(String(row.savedId));
          }
        }
        this.savedArticles = project.positions;
        this.multiMessage = this.multiHasUncertainRows
          ? 'Der Speicherstatus ist weiterhin unklar. Die betroffenen Zeilen bleiben gegen erneutes Speichern gesperrt. Bitte den Warenkorb prüfen.'
          : 'Die gespeicherten Positionen wurden bestätigt.';
        if (!this.multiHasUncertainRows) {
          draft.finalizationPending = true;
          await this.finalizeMultiPositions(projectId, draft, false);
        }
      } catch { this.multiMessage = 'Der Speicherstatus konnte nicht geladen werden. Es wurde nichts erneut gesendet.'; }
      finally { this.multiSaving = false; }
    },
    handleMultiTableKeydown(event) {
      if (event.isComposing || event.ctrlKey || event.metaKey || event.altKey) return;
      const input = event.target;
      const cell = input.closest('td');
      const row = cell?.parentElement;
      if (!row?.matches('[data-multi-row]') || !['Enter', 'ArrowUp', 'ArrowDown'].includes(event.key)) return;
      const direction = event.key === 'ArrowUp' || event.shiftKey ? -1 : 1;
      let nextRow = direction > 0 ? row.nextElementSibling : row.previousElementSibling;
      while (nextRow && !nextRow.matches('[data-multi-row]')) nextRow = direction > 0 ? nextRow.nextElementSibling : nextRow.previousElementSibling;
      const nextInput = nextRow?.children[cell.cellIndex]?.querySelector('input:not(:disabled):not([readonly]), select:not(:disabled), textarea:not(:disabled)');
      if (nextInput) {
        event.preventDefault();
        input.dispatchEvent(new Event('change', { bubbles: true }));
        nextInput.focus();
        nextInput.select?.();
      }
    },
  },
};
